import * as Sentry from '@sentry/react';
import { useEffect } from 'react';

import { bugReportStore } from './bugReportStore';

/**
 * Wires the Sentry feedback form for customers. Mounted only when
 * `useBugReportWidget()` returns `'sentry'` — the parent owns that decision so
 * this component can assume it should render unconditionally.
 *
 * Renders no trigger. The form opens only on an `openRequested` event.
 */
export function SentryFeedbackWidget() {
  // External open requests (e.g. the Support Center contact card) open the
  // Sentry feedback form programmatically. The form is created once and
  // reused, so repeated requests re-open it instead of appending a new
  // dialog to the DOM each time.
  useEffect(() => {
    let form: { open: () => void; removeFromDom: () => void } | undefined;
    let isCreatingForm = false;
    let isDisposed = false;

    const subscription = bugReportStore.on('openRequested', () => {
      if (form) {
        form.open();
        return;
      }

      const feedback = Sentry.getFeedback();
      if (!feedback || isCreatingForm) {
        return;
      }

      isCreatingForm = true;
      feedback
        .createForm()
        .then((createdForm) => {
          // The widget may unmount while the form chunk is still loading —
          // opening it then would orphan a dialog nothing can close.
          if (isDisposed) {
            return;
          }

          form = createdForm;
          createdForm.appendToDom();
          createdForm.open();
        })
        .catch(() => {
          // Form chunk failed to load — allow a retry on the next request.
          isCreatingForm = false;
        });
    });

    return () => {
      isDisposed = true;
      subscription.unsubscribe();
      form?.removeFromDom();
    };
  }, []);

  return null;
}
